import { ApiError, type AuditEvent } from '@central/api';
import { Card, PageHeader, StatusChip, formatDateTime } from '@central/ui';
import { Button, Select, Table, TextInput } from '@mantine/core';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { CustomerSelect } from '../components/CustomerSelect';
import { DataTable, Empty, LoadMore, QueryState } from '../components/parts';
import {
  actorLabel,
  auditQuery,
  auditRanges,
  describeAuditChange,
  targetLink,
  type AuditRange,
  type AuditSearch,
} from '../lib/audit';
import { mono, muted } from '../lib/labels';

const fieldText: Record<string, string> = {
  action: 'An action such as license.update, or a prefix such as license.*',
  actor: 'An email or user id',
  target: 'Such as customers/c_riverside',
};

type TextFilters = Required<Pick<AuditSearch, 'actor' | 'action' | 'target'>>;

/**
 * Audit log: every write in both apps and the agent, newest first. Filters
 * live in the URL, so a page elsewhere can link to one target's history
 * (`/audit?target=licenses/…`). Any staff role reads it.
 */
export function Audit() {
  const api = useApi();
  const navigate = useNavigate({ from: '/audit' });
  const search = useSearch({ from: '/app/audit' });
  const [draft, setDraft] = useState<TextFilters>({
    actor: search.actor ?? '',
    action: search.action ?? '',
    target: search.target ?? '',
  });
  useEffect(() => {
    setDraft({
      actor: search.actor ?? '',
      action: search.action ?? '',
      target: search.target ?? '',
    });
  }, [search.actor, search.action, search.target]);

  // The range resolves to a start time once per search, so paging keeps one window.
  const query = useMemo(() => auditQuery(search), [search]);
  const list = useInfiniteQuery({
    queryKey: keys.auditList(query),
    queryFn: ({ pageParam }) => api.audit.list({ ...query, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const fields = list.error instanceof ApiError ? (list.error.fields ?? {}) : {};
  const filtered = !!(
    search.customerId ||
    search.actor ||
    search.action ||
    search.target ||
    search.range
  );

  const apply = (next: Partial<AuditSearch>) =>
    void navigate({ search: (s) => ({ ...s, ...next }), replace: true });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    apply({
      actor: draft.actor.trim() || undefined,
      action: draft.action.trim() || undefined,
      target: draft.target.trim() || undefined,
    });
  };

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Every change in the console, the portal and from sites, newest first"
      />
      <form onSubmit={submit} noValidate aria-label="Filter the audit log">
        <Card>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: 12,
              alignItems: 'start',
            }}
          >
            <CustomerSelect
              label="Customer"
              placeholder="Any customer"
              value={search.customerId ?? ''}
              onChange={(id) => apply({ customerId: id || undefined })}
            />
            <TextInput
              label="Actor"
              placeholder="Email or user id"
              value={draft.actor}
              error={fields.actor && fieldText.actor}
              onChange={(e) => setDraft((d) => ({ ...d, actor: e.currentTarget.value }))}
            />
            <TextInput
              label="Action"
              placeholder="license.update or license.*"
              value={draft.action}
              error={fields.action && fieldText.action}
              onChange={(e) => setDraft((d) => ({ ...d, action: e.currentTarget.value }))}
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
            />
            <TextInput
              label="Target"
              placeholder="customers/c_riverside"
              value={draft.target}
              error={fields.target && fieldText.target}
              onChange={(e) => setDraft((d) => ({ ...d, target: e.currentTarget.value }))}
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
            />
            <Select
              label="Time"
              data={[
                { value: '', label: 'All time' },
                ...auditRanges.map(({ value, label }) => ({ value, label })),
              ]}
              value={search.range ?? ''}
              onChange={(v) => apply({ range: (v || undefined) as AuditRange | undefined })}
              allowDeselect={false}
            />
            <Button
              type="submit"
              color="dark"
              mt={25}
              loading={list.isFetching && !list.isFetchingNextPage}
            >
              Filter
            </Button>
          </div>
        </Card>
      </form>
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            items.length === 0 ? (
              <Empty>{filtered ? 'No events match.' : 'Nothing has been recorded yet.'}</Empty>
            ) : (
              <DataTable
                label="Audit events"
                minWidth={900}
                head={['When', 'Actor', 'Action', 'Target', 'Changes', 'IP']}
              >
                {items.map((e) => (
                  <AuditRow key={e.id} e={e} />
                ))}
              </DataTable>
            )
          }
        </QueryState>
        {list.hasNextPage && (
          <LoadMore
            what="audit events"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          />
        )}
      </Card>
    </>
  );
}

const actorTone = { staff: 'blu', portal: 'vio', agent: 'unk', system: 'unk' } as const;

/** One audit event as a row of the When, Actor, Action, Target, Changes, IP table. */
export function AuditRow({ e }: { e: AuditEvent }) {
  const link = targetLink(e.target);
  return (
    <Table.Tr style={{ verticalAlign: 'top' }}>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.at)}</Table.Td>
      <Table.Td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <span>{actorLabel(e)}</span>
          <StatusChip tone={actorTone[e.actor.kind]}>{e.actor.kind}</StatusChip>
        </div>
      </Table.Td>
      <Table.Td style={mono}>{e.action}</Table.Td>
      <Table.Td style={{ ...mono, wordBreak: 'break-all' }}>
        {link ? <Link {...link}>{e.target}</Link> : e.target}
      </Table.Td>
      <Table.Td style={{ fontSize: 13 }}>
        {e.diff?.length ? (
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {e.diff.map((d, i) => (
              <li key={i} style={{ wordBreak: 'break-word' }}>
                {describeAuditChange(d)}
              </li>
            ))}
          </ul>
        ) : (
          <span style={muted}>–</span>
        )}
      </Table.Td>
      <Table.Td style={{ ...mono, ...muted }}>{e.ip ?? '–'}</Table.Td>
    </Table.Tr>
  );
}
